import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as styled from './styles';
import { BlobThree } from '../vector';
import SocialLink from '../SocialLink';

function SocialMediaListing(props) {
  const {
    heading,
    socialMediaList,
    socialLinks,
    colorTheme,
    headingAlignment,
  } = props;

  useEffect(() => {
    const script = typeof document !== `undefined` && document.createElement('script');
    script.src = 'https://www.instagram.com/embed.js';
    script.async = true;
    typeof document !== `undefined` && document.body.appendChild(script);

    return () => {
      typeof document !== `undefined` && document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (typeof document !== `undefined` && !document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v17.0';
      script.async = true;
      document.body.appendChild(script);
    } else if (typeof window !== 'undefined' && window.FB) {
      window.FB.XFBML.parse(); // parse again if FB is already loaded
    }
  }, []);

  const containerRef = useRef(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.instgrm) {
      window.instgrm.Embeds.process();
    }
  }, []);

  useEffect(() => {
    const scriptId = 'twitter-wjs';

    if (typeof document !== `undefined` && !document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://platform.twitter.com/widgets.js';
      script.async = true;
      document.body.appendChild(script);
    } else if (typeof window !== 'undefined' && window.twttr && typeof window !== 'undefined' && window.twttr.widgets) {
      typeof window !== 'undefined' && window.twttr.widgets.load();
    }
  }, []);

  useEffect(() => {
    const scriptId = 'tiktok-embed';

    if (typeof document !== `undefined` && !document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://www.tiktok.com/embed.js';
      script.async = true;
      document.body.appendChild(script);
    } else if (typeof window !== 'undefined' && window.tiktok) {
      window.tiktok.load(); // fallback if needed
    }
  }, []);

  useEffect(() => {
    const scriptId = 'pinterest-sdk';

    if (typeof document !== `undefined` && !document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.async = true;
      script.defer = true;
      script.src = 'https://assets.pinterest.com/js/pinit.js';
      document.body.appendChild(script);
    } else if (typeof window !== 'undefined' && window.PinUtils) {
      window.PinUtils.build(); // re-initialize existing script
    }
  }, []);

  const extractTikTokId = (url) => {
    const match = url.match(/\/video\/(\d+)/);
    return match ? match[1] : '';
  };

  const getYouTubeId = (url) => {
    const match = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    );
    return match ? match[1] : '';
  };

  const getEmbed = (item) => {
    let embed;
    switch (item._type) {
      case 'socialMediaFacebook':
        embed = <div
          className="fb-post"
          data-href={item.url} // must be a public FB post URL
          data-width="500"
        ></div>;
        break;
      case 'socialMediaInstagram':
        embed = <blockquote
          className="instagram-media"
          data-instgrm-permalink="https://www.instagram.com/p/DH2sXEgtQeQ/"
          data-instgrm-version="14"
          style={{ width: '100%', maxWidth: '540px', margin: 'auto' }}
        />;
        break;
      case 'socialMediaPinterest':
        embed = <a
          data-pin-do="embedPin"
          href={item.url}
          className="pinterest-embed"
        />;
        break;
      case 'socialMediaTikTok':
        embed = <blockquote
          className="tiktok-embed"
          cite={item.url}
          data-video-id={extractTikTokId(item.url)}
          style={{ width: '100%', maxWidth: '540px', margin: 'auto' }}
        >
          <section></section>
        </blockquote>;
        break;
      case 'socialMediaX':
        embed = <blockquote
          className="twitter-tweet"
          data-lang="en"
        >
          <a href={item.url}></a>
        </blockquote>;
        break;
      case 'socialMediaYouTube':
        embed = <div className="youtube-iframe">
          <iframe
            width="100%"
            height="100%"
            src={`https://www.youtube.com/embed/${getYouTubeId(item.url)}`}
            title="YouTube video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>;
        break;
      default:
        embed = null;
    }
    return embed;
  };

  return (
    <styled.Wrapper>
      <BlobThree />
      <styled.Inner>
        <styled.HeadingContainer>
          <styled.SocialList>
            {socialLinks.map(link => (
              <SocialLink key={link.title} {...link} colorTheme={colorTheme} />
            ))}
          </styled.SocialList>
          <styled.Heading level={200} colorTheme={colorTheme}>{heading}</styled.Heading>
        </styled.HeadingContainer>
        <styled.Items>{socialMediaList.map(item => (<styled.Item>{getEmbed(item)}</styled.Item>))}</styled.Items>
      </styled.Inner>
    </styled.Wrapper>
  );
}

SocialMediaListing.propTypes = {
  heading: PropTypes.string.isRequired,
  headingAlignment: PropTypes.oneOf(['left', 'center', 'right']),
  colorTheme: PropTypes.oneOf(['blue', 'green', 'orange', 'purple', 'yellow']),
  socialMediaList: PropTypes.arrayOf(PropTypes.object),
  socialLinks: PropTypes.arrayOf(PropTypes.object),
};

export default SocialMediaListing;
